import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import PlayerFilters from './PlayerFilters';

import { isEventOfficialHsEvent, isEventPTT } from '../../../../../utils/events';
import * as eventProfileActions from '../../../../../actions/event-profile-actions';
import { showErrorPopup } from '../../../../../actions/notification-actions';
import {
    acceptanceBucketTypes,
    allPositionsForDisplay,
    allStatusesForDisplay,
    divisionFormatTypeIds,
    eventStateTypeIds,
    freeDivision,
    mainPositionForDisplay,
    needsPartnerForDisplay,
    paymentProviderTypeIds,
    playerActionOptions,
    sportTypeIds,
    teamPaymentTypeIds,
    utrTypeIds,
    waitlistForDisplay,
    withDrawnForDisplay,
} from '../../../../../utils/eventConstants';
import { getApiAxiosInstance } from '../../../../../utils/network';
import * as checkoutEventRegistrationApi from '../../../../../api/checkoutEventRegistrationApi';

import AllDivisionsList from './AllDivisionsList';
import WaitlistPlayersList from './WaitlistPlayersList';
import AcceptancePlayersList from './AcceptancePlayersList';
import { buildInitialPlayersTabState, getWaitlistMoveConfig, getWithdrawRefundConfig, mergePlayerWithWithdrawn, shouldShowCustomSplitMessage, shouldUseStripeRefundFlow, shouldUseStripeWithdrawFlow } from './utils';
import { buildPlayerListsAndCounts } from './playerListProcessors';
import SwitchDivisionFlow from '../SideBar/SwitchDivisionFlow';
import MoveToMainWarning from '../SideBar/MoveToMainWarning';
import WithdrawPopup from '../../../WithdrawPopup';
import StripeWithdrawModalV2 from '../../../StripeWithdrawModal/StripeWithdrawModalV2';
import PlayerWildcardPopup from '../../../PlayerWildcardPopup';
import { WelcomePopUp } from '../../../../../components/PopUps';
import ConfirmActionPopup from '../../../../../components/PopUps/ConfirmActionPopup';
import WaitlistPartnerPopup from '../../../../../components/PopUps/WaitlistPartnerPopup';
import PopupV3 from '../../../../../components/Popup/PopupV3';

const EMPTY_LIST = [];

function PlayersTabContentV3(props) {
    const {
        hideUtrLabel,
        isCurrentMemberAdmin,
        sendMessage,
        showRatingNotLoggedIn,
        sportTypeId,
        toggleMessagePopUp,
        handleTabClick,
        updateQueryParam,
        focusFindPlayer,
    } = props;

    const auth = useSelector(({ auth }) => auth);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { event, players, playersTabState, acceptance, invites, draws, regPaymentRefund } = useSelector(({ eventProfile }) => eventProfile);
    const { types: { tms: { division: { tournament: { acceptanceListWildcardStates } = {} } = {} } = {} } = {} } = coreUI;

    const {
        id: eventId,
        eventDivisions,
        registrationStateId,
        paymentProviderTypeId,
        playerVisibility,
        needPartnerVisibility,
        utrTypeId,
        allowOffPlatformPayments,
        eventStateId,
        membersWithoutProfile,
    } = event.data || {};

    const dispatch = useDispatch();
    const location = useLocation();
    const playerFiltersRef = useRef(null);

    const [dataType, setDataType] = useState(() => {
        const storedDataType = sessionStorage.getItem('dataType');
        return storedDataType || '';
    });
    const [showSwitchDivisionFlow, setShowSwitchDivisionFlow] = useState(false);
    const [switchDivisionData, setSwitchDivisionData] = useState({});
    const [popUpTop, setPopUpTop] = useState(false);
    const [showPartnersPopup, setShowPartnersPopup] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState();
    const [partnerOptions, setPartnerOptions] = useState([]);
    const [showWithdrawPopup, setShowWithdrawPopup] = useState(false);
    const [withdrawPlayer, setWithdrawPlayer] = useState(null);
    const [withdrawRefund, setWithdrawRefund] = useState(false);
    const [showRefundSuccess, setShowRefundSuccess] = useState(false);
    const [showWithdrawRefund, setShowWithdrawRefund] = useState(true);
    const [withdrawType, setWithdrawType] = useState(null);
    const [showStripeWithdrawPopup, setShowStripeWithdrawPopup] = useState(false);
    const [playerWildcardPopup, setPlayerWildcardPopup] = useState({ show: false, memberId: null });
    const [showSplitTeamPopUp, setShowSplitTeamPopUp] = useState();
    const [showRemovePopUp, setShowRemovePopUp] = useState();
    const [regGroupIdToRemove, setRegGroupIdToRemove] = useState(null);
    const [memberIdToRemove, setmemberIdToRemove] = useState(null);
    const [showCustomMessage, setShowCustomMessage] = useState(false);
    const [splitTeamPlayer, setSplitTeamPlayer] = useState(null);
    const [showMoveToMainWaitlistPopup, setShowMoveToMainWaitlistPopup] = useState(false);
    const [moveToMainWaitlistPlayers, setMoveToMainWaitlistPlayers] = useState(null);
    const [showMainBucketFullWarning, setShowMainBucketFullWarning] = useState(null);

    const isPaySeparately = playersTabState.teamPaymentTypeId === teamPaymentTypeIds.PAY_SEPARATELY;
    const paySeparatelyCustomMessage = `This action will unpair the team partners and move them to the 'Need Partner' section.`;
    const invitedOrPendingCustomMessage = `This will remove the invited partner from the division and place the registered partner in the 'Need Partner' section.`;
    const removeModalSubject = playersTabState.divisionFormatTypeId === divisionFormatTypeIds.SINGLES || playersTabState.teamPaymentTypeId === teamPaymentTypeIds.PAY_SEPARATELY ? 'player' : 'team';

    const searchParams = new URLSearchParams(location.search);
    const selectedDivisionId = searchParams?.get('divisionId');

    useEffect(() => {
        if (eventId && players.data === null && !players.loading) {
            dispatch(eventProfileActions.getPlayersV3(eventId));
        }
    }, [eventId, players.data, players.loading]);

    useEffect(() => {
        if (eventId && isCurrentMemberAdmin && players.withdrawn == null && !players.loading) {
            dispatch(eventProfileActions.getWithdrawnPlayers(eventId));
        }
    }, [eventId, isCurrentMemberAdmin, players.withdrawn, players.loading]);

    useEffect(() => {
        if (!eventId) {
            return;
        }
        const plan = buildInitialPlayersTabState({ eventDivisions, selectedDivisionId, event, sportTypeId });
        if (plan.divisionIdToSetInQueryParam !== null) {
            updateQueryParam('divisionId', plan.divisionIdToSetInQueryParam);
        }
        if (plan.divisionToFetchAcceptanceFor && !acceptance?.[plan.divisionToFetchAcceptanceFor]) {
            dispatch(eventProfileActions.getDivisionAcceptanceList({ eventId, divisionId: plan.divisionToFetchAcceptanceFor }));
        }
        dispatch(eventProfileActions.setPlayersTabState(plan.tabState));

        const firstPublishedDraw = (draws.data?.find(draw => draw.published)) || null;
        const storedDataType = sessionStorage.getItem('dataType');
        if ((dataType || storedDataType) && !firstPublishedDraw) {
            setDataType('');
            sessionStorage.setItem('dataType', '');
        }
    }, [eventId]);

    const isPttEvent = isEventPTT(event.data);
    const isRegistrationClosed = registrationStateId === eventStateTypeIds.REGISTRATION_CLOSED;
    const isStripe = paymentProviderTypeId === paymentProviderTypeIds.STRIPE;
    const eventHasDoublesDivision = eventDivisions?.some(div => div.divisionFormatTypeId === divisionFormatTypeIds.DOUBLES || div.divisionFormatTypeId === divisionFormatTypeIds.DINGLES);
    const firstPublishedDraw = (draws.data?.find(draw => draw.published)) || null;

    const { playersDictByMemberId } = players;

    const selectedDivision = useMemo(
        () => eventDivisions?.find(div => div.id === playersTabState.divisionId),
        [eventDivisions, playersTabState.divisionId],
    );
    const showPlayers = isCurrentMemberAdmin || !!playerVisibility;
    const showNeedPartners = showPlayers || !!needPartnerVisibility;

    const isStatusWithdrawn = playersTabState.selectedStatus?.value === withDrawnForDisplay;
    const isAllStatuses = playersTabState.selectedStatus?.label === allStatusesForDisplay;

    const isAllPositions = playersTabState.selectedStatus?.label === allPositionsForDisplay;
    const isPositionMain = playersTabState.selectedPosition?.value === mainPositionForDisplay;
    const isPositionWaitlist = playersTabState.selectedPosition?.value === waitlistForDisplay;
    const isPositionWithdrawn = playersTabState.selectedPosition?.value === withDrawnForDisplay;
    const isPositionNeedsPartner = playersTabState.selectedPosition?.value === needsPartnerForDisplay;

    const handleSetDataType = (newDataType) => {
        setDataType(newDataType);
        sessionStorage.setItem('dataType', newDataType);
    };

    const {
        teamList,
        filteredPlayers,
        withdrawnTeamList,
        playerStatuses,
        playerPositions,
        playerCount,
    } = useMemo(() => buildPlayerListsAndCounts({
        event,
        players,
        playersTabState,
        auth,
        invites,
        acceptance,
        isCurrentMemberAdmin,
        showPlayers,
        showNeedPartners,
        selectedDivision,
        utrTypeId,
    }), [
        event.data,
        selectedDivision,
        players,
        playersTabState,
        acceptance,
        auth,
        invites,
        showPlayers,
        showNeedPartners,
        isCurrentMemberAdmin,
        utrTypeId,
    ]);

    const onSwitchDivisionClick = ({ memberIds = [], divisionId }) => {
        setShowSwitchDivisionFlow(true);
        setSwitchDivisionData({ memberIds, divisionId });
    };

    const onCloseSwitchDivisionFlow = () => {
        setShowSwitchDivisionFlow(false);
        setSwitchDivisionData({});
    };

    const handleWithdrawCancel = () => {
        setPopUpTop(null);
        setShowWithdrawPopup(false);
        setShowWithdrawRefund(true);
        setWithdrawPlayer(null);
        setWithdrawRefund(false);
        setWithdrawType(null);
    };

    const onRefundCheckSelectionChange = () => {
        setWithdrawRefund(!withdrawRefund);
    };

    const closeStripeWithdrawModal = () => {
        setShowStripeWithdrawPopup(false);
    };

    const handleCustomMessage = (matchingDivisions, isAdmin, isPaySep) => {
        setShowCustomMessage(shouldShowCustomSplitMessage(matchingDivisions, isAdmin, isPaySep));
    };

    const addPartner = (memberId) => {
        if (!selectedPlayer) {
            return;
        }
        const currentDivId = event.data?.eventDivisions.find(division => division.id === playersTabState.divisionId);
        dispatch(eventProfileActions.addPartner({
            divisionId: currentDivId.id,
            memberId: selectedPlayer.memberId,
            partnerMemberId: parseInt(memberId),
        }));
        setShowPartnersPopup(false);
    };

    const handleWithdrawPlayer = async (partialWithdrawRequest) => {
        try {
            if (partialWithdrawRequest) {
                await checkoutEventRegistrationApi.partialWithdrawPlayerFromEvent(getApiAxiosInstance(), event.data.id, partialWithdrawRequest);

                if (partialWithdrawRequest.refundRequest.divisionIds.length) {
                    setShowRefundSuccess(true);
                }
            } else {
                await checkoutEventRegistrationApi.withdrawPlayerFromEvent(getApiAxiosInstance(), event.data.id, {
                    memberIds: [withdrawPlayer.memberId],
                    refundMember: withdrawRefund,
                    withdrawMember: withdrawType === 'withdraw',
                    refundReason: 'Event withdrawal requested by player/organizer.',
                });

                const freeEvent = !event.data.eventDivisions.find(ed => ed.price !== freeDivision);

                if (withdrawRefund && !freeEvent) {
                    setShowRefundSuccess(true);
                }
            }

            await dispatch(eventProfileActions.refreshEventParticipantData(eventId, {
                divisionIds: playersTabState.divisionId ? [playersTabState.divisionId] : [],
            }));

            handleWithdrawCancel();
        } catch (e) {
            const errorMessage = e.response?.data?.DisplayMessage;

            handleWithdrawCancel();
            dispatch(showErrorPopup({
                title: 'Error withdrawing player',
                message: errorMessage || 'Something went wrong while withdrawing the player.',
                error: e,
            }));
        }
    };

    const getPlayerWithMergedDivisions = (playerId) => {
        return mergePlayerWithWithdrawn(players.data, players.withdrawn, playerId);
    };

    const handleWithdrawAction = (e, playerId) => {
        const playerToWithdraw = getPlayerWithMergedDivisions(playerId);
        if (!playerToWithdraw) { return; }
        const currentDivision = event.data?.eventDivisions.find(div => div.id === playersTabState.divisionId);
        const { showRefundOption, initialWithdrawRefund } = getWithdrawRefundConfig(event.data, currentDivision, playerToWithdraw);
        if (shouldUseStripeWithdrawFlow(event.data, currentDivision, playersTabState.divisionName)) {
            dispatch(eventProfileActions.getRegPayments({ eventId: event.data.id, memberId: playerToWithdraw.memberId }));
            setShowStripeWithdrawPopup(true);
            setWithdrawPlayer(playerToWithdraw);
        } else {
            setShowWithdrawPopup(true);
            setShowWithdrawRefund(showRefundOption);
            setWithdrawPlayer(playerToWithdraw);
            setPopUpTop(e ? e.nativeEvent.pageY : null);
            setWithdrawRefund(initialWithdrawRefund);
            setWithdrawType('withdraw');
        }
    };

    const handleRefundAction = (e, playerId) => {
        const refundPlayer = getPlayerWithMergedDivisions(playerId);
        if (!refundPlayer) { return; }
        if (shouldUseStripeRefundFlow(event.data)) {
            dispatch(eventProfileActions.getRegPayments({ eventId: event.data.id, memberId: refundPlayer.memberId }));
            setShowStripeWithdrawPopup(true);
            setWithdrawPlayer(refundPlayer);
        } else {
            setShowWithdrawPopup(true);
            setWithdrawPlayer(refundPlayer);
            setPopUpTop(e ? e.nativeEvent.pageY : null);
            setWithdrawRefund(refundPlayer.refundable);
            setWithdrawType('refund');
        }
    };

    const handleTagAction = (playerId) => {
        const acceptancePlayers = acceptance?.[playersTabState.divisionId]?.buckets?.reduce((acc, bucket) => {
            return acc.concat(bucket.players);
        }, []);
        const player = acceptancePlayers?.find(p => p.playerId === playerId);
        setPlayerWildcardPopup({ show: true, memberId: player.memberId, defaultTag: player?.tag });
    };

    const handleRoundMoveAction = (playerId, selected) => {
        const player = players.data?.find(p => p.playerId === playerId);
        if (!player) { return; }
        dispatch(eventProfileActions.movePlayersToRound(event.data.id, playersTabState.divisionId, {
            memberId: player.memberId,
            roundType: selected.value,
            acceptanceType: selected.acceptanceType,
            isSpecialExempt: false,
        }));
    };

    const handleSpecialExemptAction = (playerId, selected) => {
        const player = players.data?.find(p => p.playerId === playerId);
        if (!player) { return; }
        dispatch(eventProfileActions.movePlayersToRound(event.data.id, playersTabState.divisionId, {
            memberId: player.memberId,
            roundType: acceptanceBucketTypes.MAIN,
            acceptanceType: selected.acceptanceType,
            isSpecialExempt: true,
        }));
    };

    const handleRemoveAction = (playerId) => {
        const player = players.data?.find(p => p.playerId === playerId);
        if (!player) { return; }
        const matchingDivision = player.divisions.find(division => division.id === playersTabState.divisionId);
        if (matchingDivision) {
            setShowRemovePopUp(true);
            setRegGroupIdToRemove(matchingDivision?.registrationGroupId);
            setmemberIdToRemove(player?.memberId);
        }
    };

    const handleSplitAction = (playerId) => {
        const player = players.data?.find(p => p.playerId === playerId);
        if (!player) { return; }
        const matchingDivisions = player.divisions.filter(division => division.id === playersTabState.divisionId);
        setShowSplitTeamPopUp(true);
        setSplitTeamPlayer(player);
        handleCustomMessage(matchingDivisions, isCurrentMemberAdmin, isPaySeparately);
    };

    const handleAddPartnerAction = async (playerId) => {
        const player = players.data?.find(p => p.playerId === playerId);
        if (!player) { return; }
        const partnerOptionsToAdd = new Set();
        const partnerOpts = await dispatch(eventProfileActions.getPotentialPartners(event.data.id, playersTabState.divisionId, player.memberId));
        setSelectedPlayer({ firstName: player.firstName, lastName: player.lastName, memberId: player.memberId });
        partnerOpts.members.forEach((partner) => {
            partnerOptionsToAdd.add({ id: partner.memberId, label: `${partner.firstName} ${partner.lastName}` });
        });
        setPartnerOptions(Array.from(partnerOptionsToAdd));
        setShowPartnersPopup(true);
    };

    const handleWaitlistMoveToMainAction = (playerId) => {
        const currentDivision = event.data?.eventDivisions.find(div => div.id === playersTabState.divisionId);
        const player = players.data?.find(p => p.playerId === playerId);
        if (!player) { return; }
        const playerPartners = player.divisions.find(division => division.id === playersTabState.divisionId)?.partners;
        const partner = playerPartners?.length > 0 ? players.data?.find(p => p.memberId === playerPartners[0]) : null;
        const { isMainFull, isDoubles, playerNames, memberIds } = getWaitlistMoveConfig(currentDivision, player, partner);
        setMoveToMainWaitlistPlayers({ isMainFull, isDoubles, playerNames, memberIds });
        if (isMainFull) {
            setShowMainBucketFullWarning(true);
        } else {
            setShowMoveToMainWaitlistPopup(true);
        }
    };

    const playerMenuOnSelected = async (e, playerId, selected) => {
        switch (selected.value) {
            case playerActionOptions.WITHDRAW:
                handleWithdrawAction(e, playerId);
                break;
            case playerActionOptions.REFUND:
                handleRefundAction(e, playerId);
                break;
            case playerActionOptions.MESSAGE: {
                const messagePlayer = players.data?.find(player => player.playerId === playerId);
                if (messagePlayer) { sendMessage(messagePlayer); }
                break;
            }
            case playerActionOptions.TAG:
                handleTagAction(playerId);
                break;
            case playerActionOptions.MAIN:
            case playerActionOptions.QUALIFYING:
            case playerActionOptions.ALTERNATE:
                handleRoundMoveAction(playerId, selected);
                break;
            case playerActionOptions.SPECIAL_EXEMPT:
                handleSpecialExemptAction(playerId, selected);
                break;
            case playerActionOptions.REMOVE:
                handleRemoveAction(playerId);
                break;
            case playerActionOptions.SPLIT:
                handleSplitAction(playerId);
                break;
            case playerActionOptions.ADD_PARTNER:
                await handleAddPartnerAction(playerId);
                break;
            case playerActionOptions.WAITLIST_MOVE_TO_MAIN:
                handleWaitlistMoveToMainAction(playerId);
                break;
            default:
                break;
        }
    };

    const submitPlayerWildcard = ({ memberId, wildcardStateId }) => {
        dispatch(eventProfileActions.setPlayerWildcard({
            eventId: event.data.id,
            divisionId: playersTabState.divisionId,
            memberId,
            wildcardStateId,
        }));
        closePlayerWildcardPopup();
    };

    const closePlayerWildcardPopup = () => {
        setPlayerWildcardPopup({ show: false, memberId: null });
    };

    const splitDivisionTeam = async () => {
        setShowSplitTeamPopUp(false);
        if (splitTeamPlayer) {
            const playerDivision = splitTeamPlayer.divisions.find(division => division.id === playersTabState.divisionId);
            dispatch(eventProfileActions.splitDivisionTeam(event.data.id, playerDivision.registrationGroupId, playersTabState.divisionId));
            setSplitTeamPlayer(null);
        }
    };

    const removePendingPlayer = async () => {
        setShowRemovePopUp(false);
        if (regGroupIdToRemove && memberIdToRemove) {
            dispatch(eventProfileActions.removePlayers(event.data.id, memberIdToRemove, regGroupIdToRemove, playersTabState.divisionId));
            setRegGroupIdToRemove(null);
            setmemberIdToRemove(null);
        }
    };

    const handleMoveToMain = async () => {
        await dispatch(eventProfileActions.movePlayersToMain(event.data.id, playersTabState.divisionId, moveToMainWaitlistPlayers));
        setShowMainBucketFullWarning(false);
        setShowMoveToMainWaitlistPopup(false);
    };

    const isFreeEvent = selectedDivision?.price === freeDivision;
    const freeDivisions = eventDivisions?.filter(division => division.price === freeDivision);

    const isOfficialHSEvent = isEventOfficialHsEvent(event.data);
    const isTennisSportEvent = sportTypeId === sportTypeIds.TENNIS;
    const isPickleBallSportEvent = sportTypeId === sportTypeIds.PICKLEBALL;
    const isWithdrawnPositionExcluded = isPositionMain || isPositionNeedsPartner || isPositionWaitlist;
    const isWithdrawnStatusOrPositionSelected = isAllStatuses || isAllPositions || isPositionWithdrawn || isStatusWithdrawn;
    const shouldKeepWithdrawnTeamList = !isWithdrawnPositionExcluded && isWithdrawnStatusOrPositionSelected;

    const renderPlayerList = (filterRef) => {
        const onClickPlayerDivision = (division) => {
            if (filterRef.current) {
                filterRef.current.onSelectDivisions([{
                    value: division.id,
                    label: division.name,
                    divisionName: division.name,
                }]);
            }
        };

        if (acceptance?.[playersTabState.divisionId]?.loading || players.loading || players.data === null) {
            return <p data-testid="players-tab.loading-msg">Loading players...</p>;
        }

        // ALL DIVISIONS
        if (!selectedDivision) {
            return (
                <AllDivisionsList
                    players={filteredPlayers}
                    showPlayers={showPlayers}
                    playerCount={playerCount}
                    isTennisSportEvent={isTennisSportEvent}
                    isPickleBallSportEvent={isPickleBallSportEvent}
                    isPtsEvent={isPttEvent}
                    isCurrentMemberAdmin={isCurrentMemberAdmin}
                    showRatingNotLoggedIn={showRatingNotLoggedIn}
                    utrTypeId={utrTypeId}
                    isOfficialHSEvent={isOfficialHSEvent}
                    auth={auth}
                    sendMessage={sendMessage}
                    hideUtrLabel={hideUtrLabel}
                    sportTypeId={sportTypeId}
                    playersDictByMemberId={playersDictByMemberId}
                    isFreeEvent={isFreeEvent}
                    isStripe={isStripe}
                    allowOffPlatformPayments={allowOffPlatformPayments}
                    playerMenuOnSelected={playerMenuOnSelected}
                    showRegStatus={eventHasDoublesDivision}
                    showPartner={eventHasDoublesDivision}
                    toggleMessagePopUp={toggleMessagePopUp}
                    freeDivisions={freeDivisions}
                    showNeedPartners={showNeedPartners}
                    handleTabClick={handleTabClick}
                    dataType={dataType}
                    hasPublishedDraw={!!firstPublishedDraw}
                    isEventStarted={eventStateId >= eventStateTypeIds.IN_PROGRESS}
                    onClickPlayerDivision={onClickPlayerDivision}
                />
            );
        }

        if (!acceptance?.[playersTabState.divisionId]?.loaded) {
            return (
                <WaitlistPlayersList
                    teamList={teamList}
                    withdrawnTeamList={shouldKeepWithdrawnTeamList ? withdrawnTeamList : EMPTY_LIST}
                    selectedDivision={selectedDivision}
                    showPlayers={showPlayers}
                    playerCount={playerCount}
                    isTennisSportEvent={isTennisSportEvent}
                    isPickleBallSportEvent={isPickleBallSportEvent}
                    isPtsEvent={isPttEvent}
                    isCurrentMemberAdmin={isCurrentMemberAdmin}
                    showRatingNotLoggedIn={showRatingNotLoggedIn}
                    utrTypeId={utrTypeId}
                    isOfficialHSEvent={isOfficialHSEvent}
                    auth={auth}
                    sendMessage={sendMessage}
                    hideUtrLabel={hideUtrLabel}
                    sportTypeId={sportTypeId}
                    playersDictByMemberId={playersDictByMemberId}
                    currentDivision={selectedDivision}
                    isFreeEvent={isFreeEvent}
                    isStripe={isStripe}
                    allowOffPlatformPayments={allowOffPlatformPayments}
                    playerMenuOnSelected={playerMenuOnSelected}
                    showRegStatus={eventHasDoublesDivision}
                    showPartner={eventHasDoublesDivision}
                    toggleMessagePopUp={toggleMessagePopUp}
                    onSwitchDivisionClick={onSwitchDivisionClick}
                />
            );
        }

        return (
            <AcceptancePlayersList
                acceptanceData={acceptance?.[playersTabState.divisionId]}
                isRegistrationClosed={isRegistrationClosed}
                selectedDivision={selectedDivision}
                showPlayers={showPlayers}
                playerCount={playerCount}
                isTennisSportEvent={isTennisSportEvent}
                isPickleBallSportEvent={isPickleBallSportEvent}
                isPttEvent={isPttEvent}
                isCurrentMemberAdmin={isCurrentMemberAdmin}
                showRatingNotLoggedIn={showRatingNotLoggedIn}
                utrTypeId={utrTypeId}
                isOfficialHSEvent={isOfficialHSEvent}
                auth={auth}
                sendMessage={sendMessage}
                hideUtrLabel={hideUtrLabel}
                sportTypeId={sportTypeId}
                playersDictByMemberId={playersDictByMemberId}
                currentDivision={selectedDivision}
                isStripe={isStripe}
                allowOffPlatformPayments={allowOffPlatformPayments}
                playerMenuOnSelected={playerMenuOnSelected}
                toggleMessagePopUp={toggleMessagePopUp}
                showNeedPartners={showNeedPartners}
                onSwitchDivisionClick={onSwitchDivisionClick}
            />
        );
    };

    return (
        <>
            <div className="row">
                <div className="col">
                    {eventDivisions && (
                        <PlayerFilters
                            ref={playerFiltersRef}
                            eventDivisions={eventDivisions}
                            membersWithoutProfile={membersWithoutProfile}
                            user={auth?.user}
                            sportTypeId={sportTypeId}
                            isCurrentMemberAdmin={isCurrentMemberAdmin}
                            playerStatuses={playerStatuses}
                            playerPositions={playerPositions}
                            playersLoaded={!!players.data && players?.data?.length > 0}
                            selectedDivisionId={selectedDivisionId}
                            dataType={dataType}
                            setDataType={handleSetDataType}
                            updateQueryParam={updateQueryParam}
                            hasPublishedDraw={!!firstPublishedDraw}
                            focusFindPlayer={focusFindPlayer}
                        />
                    )}
                    {renderPlayerList(playerFiltersRef)}
                </div>
            </div>
            {showSwitchDivisionFlow && (
                <SwitchDivisionFlow
                    memberIds={switchDivisionData.memberIds}
                    fromDivisionId={switchDivisionData.divisionId}
                    onClose={onCloseSwitchDivisionFlow}
                />
            )}
            {showWithdrawPopup && (
                <WithdrawPopup
                    closePopup={handleWithdrawCancel}
                    confirmPopup={handleWithdrawPlayer}
                    player={withdrawPlayer}
                    currentDivisionId={playersTabState.divisionId}
                    withdrawRefund={withdrawRefund}
                    showWithdrawRefund={showWithdrawRefund}
                    withdrawType={withdrawType}
                    onRefundCheckSelectionChange={onRefundCheckSelectionChange}
                    topPoint={popUpTop}
                    verifiedEvent={event.data?.utrTypeId === utrTypeIds.SANCTIONED}
                    allowOffPlatformPayments={event.data?.allowOffPlatformPayments}
                />
            )}
            {showRefundSuccess && (
                <WelcomePopUp
                    popupClass="popup-sm"
                    welcomeTitles={{
                        title: 'Refund Successful',
                        description: 'The refund will be processed within 24-48 hours.',
                    }}
                    onCloseClick={() => {
                        setShowRefundSuccess(false);
                    }}
                    onCtaClick={() => {
                        setShowRefundSuccess(false);
                    }}
                />
            )}
            {showStripeWithdrawPopup && withdrawPlayer && (
                <StripeWithdrawModalV2
                    onCloseClick={closeStripeWithdrawModal}
                    withdrawPlayer={withdrawPlayer}
                    divisions={(event.data?.eventDivisions) || []}
                    currency={(event.data?.currency) || {}}
                    regPaymentRefund={regPaymentRefund || {}}
                    confirmPopup={handleWithdrawPlayer}
                    event={event.data}
                />
            )}
            {playerWildcardPopup.show && (
                <PlayerWildcardPopup
                    memberId={playerWildcardPopup.memberId}
                    onCloseClick={closePlayerWildcardPopup}
                    onSubmit={submitPlayerWildcard}
                    options={acceptanceListWildcardStates}
                    defaultValues={{ wildcardStateId: playerWildcardPopup.defaultTag }}
                />
            )}
            {showSplitTeamPopUp && (
                <ConfirmActionPopup
                    confirmationTitle={`Split this team`}
                    confirmationMessage={showCustomMessage ? paySeparatelyCustomMessage : invitedOrPendingCustomMessage}
                    onCloseClick={() => {
                        setShowSplitTeamPopUp(false);
                    }}
                    onCtaClick={splitDivisionTeam}
                />
            )}
            {showRemovePopUp && (
                <ConfirmActionPopup
                    confirmationTitle={`Remove ${removeModalSubject}`}
                    confirmationMessage={`This action will remove the added ${removeModalSubject} from this event division.`}
                    onCloseClick={() => {
                        setShowRemovePopUp(false);
                    }}
                    onCtaClick={removePendingPlayer}
                />
            )}
            {showPartnersPopup && (
                <WaitlistPartnerPopup
                    onCloseClick={() => setShowPartnersPopup(false)}
                    onSubmit={addPartner}
                    selectedPlayer={selectedPlayer}
                    partnerOptions={partnerOptions}
                />
            )}
            {showMoveToMainWaitlistPopup && (
                <PopupV3
                    title={`Move Player${moveToMainWaitlistPlayers?.isDoubles ? 's' : ''} to Main`}
                    message={`Move ${moveToMainWaitlistPlayers?.playerNames} from Waitlist to Main?`}
                    confirmButtonLabel="Move"
                    onConfirmClick={handleMoveToMain}
                    showCancelButton
                    cancelButtonLabel="Cancel"
                    onCancelClick={() => setShowMoveToMainWaitlistPopup(false)}
                    isOpen
                />
            )}
            {showMainBucketFullWarning && (
                <MoveToMainWarning
                    onClose={() => {
                        setShowMainBucketFullWarning(false);
                        setShowMoveToMainWaitlistPopup(false);
                    }}
                    onConfirm={handleMoveToMain}
                    isDoubles={playersTabState.divisionFormatTypeId === divisionFormatTypeIds.DOUBLES}
                />
            )}
        </>
    );
}

PlayersTabContentV3.propTypes = {
    hideUtrLabel: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    sendMessage: PropTypes.func,
    showRatingNotLoggedIn: PropTypes.bool,
    sportTypeId: PropTypes.number,
    toggleMessagePopUp: PropTypes.func,
    handleTabClick: PropTypes.func,
    updateQueryParam: PropTypes.func.isRequired,
    focusFindPlayer: PropTypes.bool,
};

PlayersTabContentV3.defaultProps = {
    hideUtrLabel: false,
    isCurrentMemberAdmin: false,
    sendMessage: () => { },
    toggleMessagePopUp: () => { },
    handleTabClick: () => { },
    showRatingNotLoggedIn: false,
    sportTypeId: sportTypeIds.TENNIS,
    focusFindPlayer: false,
};

export default withRouter(PlayersTabContentV3);
